import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ArrowLeft, Check, Copy, ExternalLink, Loader2, Plus,
  RefreshCw, Sparkles, Trash2, Wand2, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../../lib/api";
import MediaPicker from "../MediaPicker";
import LocalInventoryPanel from "./LocalInventoryPanel";
import { AdminHeader, Badge, Field, Panel, Row, Table, borderStyle, inputCls } from "../ui";

const CONDITIONS = [
  ["new", "New"],
  ["new-old-stock", "New old stock (superseded / old packaging)"],
  ["display", "Ex-display"],
  ["superseded", "Superseded"],
  ["used", "Used"],
];

const TONE = { ready: "green", warnings: "gold", blocked: "red" };

const toCents = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
const toDollars = (c) => (c === null || c === undefined || c === "" ? "" : (Number(c) / 100).toFixed(2));

const BLANK = {
  sku: "", title: "", brand: "", mpn: "", gtin: "", condition_note: "new",
  price: "", rrp: "", cost: "", min_price: "", qty: "", weight_kg: "",
  product_type: "", fitment_text: "", description: "", location: "",
  images: [], published: true, feed_excluded: false,
};

function fromProduct(p) {
  return {
    id: p.id,
    sku: p.sku || "",
    title: p.title || p.name || "",
    brand: p.brand || "",
    mpn: p.mpn || "",
    gtin: p.gtin || "",
    condition_note: p.condition_note || "new",
    price: toDollars(p.price_cents),
    rrp: toDollars(p.rrp_cents),
    cost: toDollars(p.cost_cents),
    min_price: toDollars(p.min_price_cents),
    qty: p.qty ?? "",
    weight_kg: p.weight_grams ? (p.weight_grams / 1000).toString() : "",
    product_type: p.product_type || "",
    fitment_text: p.fitment_text || (p.fitment || []).map((f) => f.raw).filter(Boolean)[0] || "",
    description: p.description || "",
    location: p.location || "",
    images: p.images || [],
    published: !!p.published,
    feed_excluded: !!p.feed_excluded,
    merchant_issues: p.merchant_issues || [],
  };
}

function toPayload(f) {
  const weight = parseFloat(f.weight_kg);
  return {
    sku: f.sku.trim(),
    title: f.title.trim(),
    brand: f.brand.trim(),
    mpn: f.mpn.trim(),
    gtin: f.gtin.trim(),
    condition_note: f.condition_note,
    price_cents: toCents(f.price),
    rrp_cents: toCents(f.rrp),
    cost_cents: toCents(f.cost),
    min_price_cents: toCents(f.min_price),
    qty: f.qty === "" ? 0 : parseInt(f.qty, 10) || 0,
    weight_grams: Number.isFinite(weight) && weight > 0 ? Math.round(weight * 1000) : null,
    product_type: f.product_type.trim(),
    fitment_text: f.fitment_text.trim(),
    description: f.description.trim(),
    location: f.location.trim(),
    images: f.images,
    published: f.published,
    feed_excluded: f.feed_excluded,
  };
}

/** What Google still needs, grouped so blockers are unmissable. */
function IssueList({ issues }) {
  if (!issues?.length) {
    return (
      <p className="flex items-center gap-2 text-sm text-emerald-400">
        <Check className="h-4 w-4" /> Ready for Google - nothing outstanding.
      </p>
    );
  }
  const errors = issues.filter((i) => i.severity === "error");
  const warns = issues.filter((i) => i.severity === "warning");
  return (
    <div className="space-y-3" data-testid="merchant-issues">
      {errors.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-red-400">
            Blocking - withheld from Google ({errors.length})
          </p>
          <ul className="space-y-1">
            {errors.map((i) => (
              <li key={i.code} className="flex gap-2 text-sm text-red-200">
                <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />{i.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {warns.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-400">
            Worth fixing ({warns.length})
          </p>
          <ul className="space-y-1">
            {warns.map((i) => (
              <li key={i.code} className="flex gap-2 text-sm text-[color:var(--nq-stone)]">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />{i.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function StockAdmin() {
  const [products, setProducts] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [readiness, setReadiness] = useState("");
  const [form, setForm] = useState(null);      // null = list view
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [picker, setPicker] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, h] = await Promise.all([
        api.get("/admin/catalog/products", { params: { q: q || undefined, readiness: readiness || undefined, limit: 500 } }),
        api.get("/feeds/health"),
      ]);
      setProducts(list.data.products || []);
      setHealth(h.data);
    } catch (err) {
      toast.error("Could not load stock", { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [q, readiness]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  /** Let the AI read the photo and write the listing. Works before the first save. */
  const autoWrite = useCallback(async (current) => {
    const f = current || form;
    if (!f) return;
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/catalog/draft-copy", toPayload(f));
      const d = data.draft;
      setForm((prev) => ({
        ...prev,
        title: d.title || prev.title,
        description: d.description || prev.description,
        fitment_text: d.fitment_text || prev.fitment_text,
        product_type: prev.product_type || d.product_type || "",
      }));
      toast.success(d.used_photo ? "Written from your photo" : "Listing drafted", {
        description: d.needs_fitment
          ? "It couldn't tell which vehicle this fits - add that yourself."
          : "Check it over, then save.",
      });
    } catch (err) {
      toast.error("The AI couldn't draft this", { description: err?.response?.data?.detail || err.message });
    } finally {
      setDrafting(false);
    }
  }, [form]);

  async function save() {
    if (!form.sku.trim() || !form.title.trim()) {
      toast.error("SKU and title are required");
      return;
    }
    if (!toCents(form.price)) {
      toast.error("A price is required", { description: "Google rejects products without a price." });
      return;
    }
    if (String(form.qty).trim() === "") {
      toast.error("Quantity on hand is required", { description: "Enter 0 if you're out of stock." });
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      const { data } = form.id
        ? await api.patch(`/admin/catalog/products/${form.id}`, payload)
        : await api.post("/admin/catalog/products", payload);
      const saved = data.product;
      toast.success(form.id ? "Product updated" : "Product added", {
        description: saved.in_feed ? "It's in the Google feed." : "Not in the Google feed yet - see what's outstanding.",
      });
      setForm({ ...fromProduct(saved), merchant_issues: saved.merchant_issues || [] });
      load();
    } catch (err) {
      toast.error("Could not save", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  }

  async function remove(p) {
    if (!window.confirm(`Delete "${p.title || p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/catalog/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error("Could not delete", { description: err?.response?.data?.detail || err.message });
    }
  }

  const counts = health?.readiness || {};
  const feedUrl = health?.feed_url || "";

  const summary = useMemo(() => ([
    { key: "", label: `All (${health?.products_total ?? products.length})` },
    { key: "ready", label: `Ready (${counts.ready ?? 0})` },
    { key: "warnings", label: `Warnings (${counts.warnings ?? 0})` },
    { key: "blocked", label: `Blocked (${counts.blocked ?? 0})` },
  ]), [health, counts, products.length]);

  // ---------------------------------------------------------------- form view
  if (form) {
    return (
      <div className="space-y-6">
        {picker && (
          <MediaPicker
            onClose={() => setPicker(false)}
            onPick={(m) => {
              const next = { ...form, images: [...(form.images || []), m.url] };
              setForm(next);
              setPicker(false);
              // First photo on a blank listing: just write it, don't make them ask.
              if (!next.title.trim()) autoWrite(next);
            }}
          />
        )}
        <AdminHeader
          title={form.id ? "Edit product" : "Add a product"}
          subtitle="Everything Google Merchant Center needs for this item"
        >
          <button onClick={() => { setForm(null); load(); }} className="btn-ghost inline-flex items-center gap-2" data-testid="stock-back">
            <ArrowLeft className="h-4 w-4" /> Back to stock
          </button>
          <button onClick={() => autoWrite()} disabled={drafting} className="btn-outline-nq inline-flex items-center gap-2" data-testid="stock-ai-write">
            {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Write it for me
          </button>
          <button onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2" data-testid="stock-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {form.id ? "Save changes" : "Add product"}
          </button>
        </AdminHeader>

        <Panel className="border-l-2" style={{ borderLeftColor: "var(--nq-gold)" }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="h-4 w-4 gold-text" /> AI assistant
              </p>
              <p className="mt-1 text-xs text-[color:var(--nq-stone)]">
                Add a photo and it writes the title, description and category for you - it reads
                the actual photo. Every image you upload is also resized to 1600x1600 on a clean
                background automatically, so Google accepts it. You just check it over and save.
              </p>
            </div>
            <button
              onClick={() => autoWrite()}
              disabled={drafting}
              className="btn-gold inline-flex items-center gap-2 text-sm"
              data-testid="stock-ai-write-panel"
            >
              {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              {drafting ? "Writing..." : "Write this listing for me"}
            </button>
          </div>
        </Panel>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Panel>
              <h3 className="mb-4 font-semibold">Photos</h3>
              <div className="flex flex-wrap gap-3">
                {(form.images || []).map((img, i) => (
                  <div key={img + i} className="relative">
                    <img src={mediaUrl(img, 320)} alt="" className="h-24 w-24 rounded-lg object-cover" />
                    {i === 0 && (
                      <span className="absolute left-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold gold-text">
                        MAIN
                      </span>
                    )}
                    <button
                      onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))}
                      className="absolute -right-2 -top-2 rounded-full bg-red-600 p-1 text-white"
                      aria-label="Remove image"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setPicker(true)}
                  className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-xs text-[color:var(--nq-stone)] hover:border-[color:var(--nq-gold)]"
                  style={borderStyle}
                  data-testid="stock-add-image"
                >
                  <Plus className="h-5 w-5" /> Upload
                </button>
              </div>
              <p className="mt-3 text-xs text-[color:var(--nq-stone)]">
                First photo is the one Google shows. Snap it on any plain background and upload it -
                every photo is automatically squared off and resized to 1600x1600 so Google accepts
                it. Just don't use a photo with a watermark or another shop's logo on it.
              </p>
            </Panel>

            <Panel>
              <h3 className="mb-4 font-semibold">The part</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="SKU / stock code *" hint="Your own code. Must be unique.">
                  <input value={form.sku} onChange={set("sku")} className={inputCls} data-testid="stock-sku" />
                </Field>
                <Field label="Brand *" hint="EFS, Dobinsons, Mahindra...">
                  <input value={form.brand} onChange={set("brand")} className={inputCls} data-testid="stock-brand" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Title *" hint="Lead with the vehicle: buyers search &quot;Navara D21 torsion bars&quot;, not &quot;torsion bar set&quot;.">
                    <input value={form.title} onChange={set("title")} maxLength={150} className={inputCls} data-testid="stock-title" />
                  </Field>
                  <p className="mt-1 text-xs text-[color:var(--nq-stone)]">{form.title.length}/150</p>
                </div>
                <Field label="Manufacturer part number" hint="Strongly improves Google matching.">
                  <input value={form.mpn} onChange={set("mpn")} className={inputCls} data-testid="stock-mpn" />
                </Field>
                <Field label="Barcode / GTIN" hint="Leave blank if there isn't one - never invent one.">
                  <input value={form.gtin} onChange={set("gtin")} className={inputCls} data-testid="stock-gtin" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Fits which vehicles?" hint="Plain English is fine - e.g. &quot;Suits Nissan Navara D21 4WD 86-97&quot;.">
                    <input value={form.fitment_text} onChange={set("fitment_text")} className={inputCls} data-testid="stock-fitment" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Description" hint="What it is, what's included, what condition. This is what Google matches searches against.">
                    <textarea rows={5} value={form.description} onChange={set("description")} className={inputCls} data-testid="stock-description" />
                  </Field>
                </div>
                <Field label="Category" hint="Suspension, Underbody Protection...">
                  <input value={form.product_type} onChange={set("product_type")} className={inputCls} />
                </Field>
                <Field label="Stock condition">
                  <select value={form.condition_note} onChange={set("condition_note")} className={inputCls} data-testid="stock-condition">
                    {CONDITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
              </div>
            </Panel>

            <Panel>
              <h3 className="mb-4 font-semibold">Price &amp; stock</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Selling price (AUD, inc GST) *">
                  <input value={form.price} onChange={set("price")} inputMode="decimal" placeholder="0.00" className={inputCls} data-testid="stock-price" />
                </Field>
                <Field label="RRP / was price" hint="Shows the customer their saving.">
                  <input value={form.rrp} onChange={set("rrp")} inputMode="decimal" placeholder="0.00" className={inputCls} data-testid="stock-rrp" />
                </Field>
                <Field label="Quantity on hand *" hint="Stops the site selling the same one twice.">
                  <input value={form.qty} onChange={set("qty")} inputMode="numeric" className={inputCls} data-testid="stock-qty" />
                </Field>
                <Field label="Weight (kg)" hint="Needed to work out freight.">
                  <input value={form.weight_kg} onChange={set("weight_kg")} inputMode="decimal" className={inputCls} data-testid="stock-weight" />
                </Field>
              </div>

              <div className="mt-5 rounded-lg border p-4" style={{ ...borderStyle, background: "rgba(255,255,255,0.02)" }}>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider gold-text">
                  Private - never shown on the website
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Cost price">
                    <input value={form.cost} onChange={set("cost")} inputMode="decimal" className={inputCls} data-testid="stock-cost" />
                  </Field>
                  <Field label="Lowest you'd accept" hint="Warns you if the price drops below it.">
                    <input value={form.min_price} onChange={set("min_price")} inputMode="decimal" className={inputCls} />
                  </Field>
                  <Field label="Shelf / bin">
                    <input value={form.location} onChange={set("location")} className={inputCls} />
                  </Field>
                </div>
              </div>
            </Panel>
          </div>

          <div className="space-y-6">
            <Panel>
              <h3 className="mb-3 font-semibold">Google Merchant status</h3>
              {form.id ? (
                <IssueList issues={form.merchant_issues} />
              ) : (
                <p className="text-sm text-[color:var(--nq-stone)]">
                  Save the product and this shows exactly what Google still needs.
                </p>
              )}
            </Panel>

            <Panel>
              <h3 className="mb-3 font-semibold">Visibility</h3>
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked={form.published} onChange={set("published")} className="mt-1" data-testid="stock-published" />
                <span>
                  <strong>Live on the website</strong>
                  <span className="block text-xs text-[color:var(--nq-stone)]">
                    Must be on for the product to reach Google.
                  </span>
                </span>
              </label>
              <label className="mt-4 flex items-start gap-3 text-sm">
                <input type="checkbox" checked={form.feed_excluded} onChange={set("feed_excluded")} className="mt-1" />
                <span>
                  <strong>Hide from Google</strong>
                  <span className="block text-xs text-[color:var(--nq-stone)]">
                    Sell it on the website only - keep it out of the feed.
                  </span>
                </span>
              </label>
            </Panel>
          </div>
        </div>

        <div
          className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-end gap-3 border-t px-4 py-4 md:-mx-8 md:px-8"
          style={{ ...borderStyle, background: "rgba(14,18,16,0.92)", backdropFilter: "blur(8px)" }}
        >
          <button onClick={() => { setForm(null); load(); }} className="btn-ghost inline-flex items-center gap-2" data-testid="stock-back-bottom">
            <ArrowLeft className="h-4 w-4" /> Back to stock
          </button>
          <button onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2" data-testid="stock-save-bottom">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {form.id ? "Save changes" : "Add product"}
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- list view
  return (
    <div className="space-y-6">
      <AdminHeader title="Stock &amp; Google feed" subtitle="Products for sale, and whether Google will accept them">
        <button onClick={load} className="btn-ghost inline-flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
        <button onClick={() => setForm({ ...BLANK })} className="btn-gold inline-flex items-center gap-2" data-testid="stock-new">
          <Plus className="h-4 w-4" /> Add product
        </button>
      </AdminHeader>

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider gold-text">Google Merchant feed</p>
            <p className="mt-1 text-sm">
              <strong data-testid="feed-in-count">{health?.in_feed ?? "-"}</strong> of{" "}
              <strong>{health?.products_total ?? "-"}</strong> products are being sent to Google
              {health?.merchant_id ? ` (account ${health.merchant_id})` : ""}.
            </p>
            {health && !health.checkout_enabled && (
              <p className="mt-1 text-xs text-red-300">
                Checkout is off, so Google will reject in-stock products. Turn on payments first.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { navigator.clipboard?.writeText(feedUrl); toast.success("Feed URL copied"); }}
              className="btn-ghost inline-flex items-center gap-2 text-xs"
              data-testid="copy-feed-url"
            >
              <Copy className="h-3.5 w-3.5" /> Copy feed URL
            </button>
            <a href={feedUrl} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 text-xs">
              <ExternalLink className="h-3.5 w-3.5" /> View feed
            </a>
          </div>
        </div>
      </Panel>

      <LocalInventoryPanel />

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, SKU, brand or part number"
          className={`${inputCls} max-w-sm`}
          data-testid="stock-search"
        />
        <div className="flex flex-wrap gap-2">
          {summary.map((s) => (
            <button
              key={s.key || "all"}
              onClick={() => setReadiness(s.key)}
              className={`rounded-lg border px-3 py-1.5 text-xs transition ${
                readiness === s.key ? "border-[color:var(--nq-gold)] gold-text" : "text-[color:var(--nq-stone)]"
              }`}
              style={readiness === s.key ? undefined : borderStyle}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Panel><p className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading stock...</p></Panel>
      ) : products.length === 0 ? (
        <Panel><p className="text-sm text-[color:var(--nq-stone)]">No products match.</p></Panel>
      ) : (
        <Table head={["Photo", "Product", "Price", "Qty", "Google", "Actions"]}>
          {products.map((p) => {
            const errs = (p.merchant_issues || []).filter((i) => i.severity === "error").length;
            return (
              <Row key={p.id} testid="stock-row">
                <td className="p-3">
                  {p.images?.[0] || p.image ? (
                    <img src={mediaUrl(p.images?.[0] || p.image, 160)} alt="" className="h-12 w-12 rounded object-cover" />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded border text-[10px] text-red-300" style={borderStyle}>
                      no photo
                    </div>
                  )}
                </td>
                <td className="p-3">
                  <button onClick={() => setForm(fromProduct(p))} className="text-left font-medium hover:text-[color:var(--nq-gold)]">
                    {p.title || p.name}
                  </button>
                  <p className="text-xs text-[color:var(--nq-stone)]">
                    {p.sku}{p.brand ? ` · ${p.brand}` : ""}{p.mpn ? ` · ${p.mpn}` : ""}
                  </p>
                </td>
                <td className="p-3 text-sm">
                  {p.price_cents ? `$${toDollars(p.price_cents)}` : <span className="text-red-300">no price</span>}
                  {p.rrp_cents && p.price_cents && p.rrp_cents > p.price_cents && (
                    <span className="ml-1 text-xs text-[color:var(--nq-stone)] line-through">${toDollars(p.rrp_cents)}</span>
                  )}
                </td>
                <td className="p-3 text-sm">
                  {(p.qty ?? 0) > 0 ? p.qty : <Badge tone="red">Sold out</Badge>}
                </td>
                <td className="p-3">
                  <Badge tone={TONE[p.merchant_readiness] || "neutral"}>
                    {p.in_feed ? "In feed" : errs > 0 ? `${errs} blocking` : "Not sent"}
                  </Badge>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => remove(p)} className="text-[color:var(--nq-stone)] hover:text-red-400" aria-label="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </Row>
            );
          })}
        </Table>
      )}
    </div>
  );
}
